/**
 * useTPWorkbook
 *
 * Encapsulates all workbook upload state and parse logic for TP generation.
 * Used by GenerateTPPage.
 * so the behaviour is identical in both flows.
 */
import { useRef, useState } from 'react'
import type { TPWorkbookResult } from '@/lib/tpWorkbookParser'
import { parseTPWorkbookForClass, type TPClass } from '@/lib/tpWorkbookParserClass'

export interface UseTPWorkbookReturn {
  workbookInputRef: React.RefObject<HTMLInputElement>
  workbookResult: TPWorkbookResult | null
  workbookFileName: string | null
  workbookParsing: boolean
  workbookError: string | null
  handleWorkbookUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  clearWorkbook: () => void
  resetWorkbook: () => void  // full reset (same as clear but exported by a clearer name)
}

export function useTPWorkbook(tpClass: TPClass = 'DNV'): UseTPWorkbookReturn {
  const workbookInputRef = useRef<HTMLInputElement>(null)
  const [workbookResult, setWorkbookResult] = useState<TPWorkbookResult | null>(null)
  const [workbookFileName, setWorkbookFileName] = useState<string | null>(null)
  const [workbookParsing, setWorkbookParsing] = useState(false)
  const [workbookError, setWorkbookError] = useState<string | null>(null)

  const handleWorkbookUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.match(/\.xlsx?$/i)) {
      setWorkbookError('Please upload an .xlsx file.')
      return
    }
    setWorkbookError(null)
    setWorkbookResult(null)
    setWorkbookFileName(file.name)
    setWorkbookParsing(true)
    try {
      const result = await parseTPWorkbookForClass(file, tpClass)
      setWorkbookResult(result)
    } catch (err: any) {
      setWorkbookError(err.message || 'Failed to parse workbook.')
      setWorkbookFileName(null)
    } finally {
      setWorkbookParsing(false)
    }
  }

  const clearWorkbook = () => {
    setWorkbookResult(null)
    setWorkbookFileName(null)
    setWorkbookError(null)
    if (workbookInputRef.current) workbookInputRef.current.value = ''
  }

  return {
    workbookInputRef,
    workbookResult,
    workbookFileName,
    workbookParsing,
    workbookError,
    handleWorkbookUpload,
    clearWorkbook,
    resetWorkbook: clearWorkbook,
  }
}
